// Tiny localStorage cache with TTL. SSR-safe (no-op if window absent).

interface CacheEntry<T> {
  data: T
  ts: number
}

export function readCache<T>(key: string, ttlMs: number): T | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw) as Partial<CacheEntry<T>>
    // Validate shape — corrupt cache (string ts, missing data, etc.) would
    // otherwise return garbage data and crash callers downstream.
    if (!entry || typeof entry.ts !== 'number' || entry.data === undefined) {
      window.localStorage.removeItem(key)
      return null
    }
    if (Date.now() - entry.ts > ttlMs) {
      window.localStorage.removeItem(key)
      return null
    }
    return entry.data as T
  } catch {
    return null
  }
}

export function writeCache<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() } satisfies CacheEntry<T>))
  } catch {
    // Quota exceeded or storage disabled — silently skip caching.
  }
}

export function clearCache(key: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}
